import React, { useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';
import ReactSelect from 'react-select';
import PropTypes, { any } from 'prop-types';

import { required } from '../../../../utils/form-validation';
import { getAllowedDrawEliminationTypes, getAllowedFormatTypes, getAllowedScoringFormats, getDefaultDivision, getEventCurrencySymbol, isPickleBallSportTypeEvent, isSinglesFormat, getAllowedGenderTypes, isDinglesOrDoublesFormat, getDoublesOrDinglesIndex, getAgeRestrictedDivisionIndex, EventTypes } from '../../../../utils/events';
import { divisionGenderTypes, acceptanceRankingValues, ballTypes, ballTypeValues, EventVerifiedTypes, acceptanceValues, teamPaymentTypeIds, eventGenderTypeIds, sportTypeIds, paymentProviderTypeIds, DefaultTimeZone, divisionFormatTypeIds, ANNUAL_POWER_ONLY_LABEL, utrTypeIds, EventHsTypes } from '../../../../utils/eventConstants';
import Tip from '../../../Tip';
import { isHighSchoolClub, isPersonalClub } from '../../../../utils/clubs';
import {
    Input,
    ToggleSwitch,
    InputGroupPre,
    Select,
    UTRRangeSliderSmall,
    PBRRangeSliderSmall,
    EventDivisionAgeRangeSliderSmall,
    CheckBox,
    DateTimePicker,
} from '../../../Form';
import DivisionAcceptanceList from '../Fields/Division/DivisionAcceptanceList';
import { validateMinEntryFee, validateCancellationDate, validateMaxRefundDate, validateSpecialDivisionDate, checkRemainingDivisionsBallTypeValue } from './divisionValidation';
import { eventDivisionAgeSliderDictionary, ratingTypes, minUtrValue, minUtrpValue, maxUtrValue, maxUtrpValue, RestrictAgeByDateTypeIds } from '../../../../utils/constants';
import { errorMessage } from '../../../../utils/displayMessageConstants';
import links from '../../../../../config/links';
import { ConfirmActionPopup } from '../../../PopUps';
import useIsMobile from '../../../hooks/useIsMobile';
import { useStatsig } from '../../../hooks/useStatsig';
import CloseRegistrationConfirmPopup from './CloseRegistrationConfirmPopup';
import { DoNotShowCloseRegistrationPopup, generateLocalStorageKey } from '../../../../utils/eventEdit';

function Divisions(props) {
    const {
        field,
        formApi,
        isFlexLeagueEvent,
        allowOffPlatformPayments,
        currency,
        editing,
        registeredPlayers,
        clubTierType,
        isDemoEvent,
        paymentsEnabled,
        userIsPowered,
        showTip,
        isTemplate,
        onChange,
        countryCode,
        paymentProviderSetupType,
        minimumEntryFee,
        formattedMinimumEntryFee,
        allowZeroEntryFee,
        minimumOrZeroEntryFeeErrorMessage,
        minimumEntryFeeErrorMessage,
        sportTypeId,
        showDateError,
        eventTypeId,
    } = props;

    const coreUI = useSelector(({ coreUI }) => coreUI);
    const auth = useSelector(({ auth }) => auth);
    const { checkGate, featureGates } = useStatsig();

    const [openDivisionIndex, setOpenDivisionIndex] = useState(0);
    const [deleteConfirmation, setDeleteConfirmation] = useState(null);
    const [closeRegistrationConfirmation, setCloseRegistrationConfirmation] = useState(null);
    const cardRefs = useRef([]);
    const { isMobileWidth } = useIsMobile();

    const isVerifiedEvent = parseInt(formApi.values.$verified) === utrTypeIds.SANCTIONED;
    const isPickleballEvent = isPickleBallSportTypeEvent(sportTypeId);
    const isDinglesEnabled = checkGate(featureGates.DINGLES);
    const genderTypes = getAllowedGenderTypes(coreUI);
    const formatTypes = getAllowedFormatTypes({
        coreUI,
        isDinglesEnabled,
        isVerifiedEvent,
    });
    const {
        coachingTypes,
        drawEliminationTypes,
        officialsTypes,
        scoringTypes,
        singlePointScoringTypes,
        acceptanceListRatingTypes,
        restrictAgeByDateTypes,
    } = coreUI?.types?.tms?.division?.tournament || {};
    const { teamPaymentTypes } = coreUI?.types?.tms?.division || {};
    const clubIsHighSchool = isHighSchoolClub(clubTierType);
    const { values, setValue } = formApi;

    const showPoweredOnly = checkGate(featureGates.SHOW_EVENT_DIVISION_POWERED_ONLY) && !isPickleballEvent;
    const isTimeBasedFormatEnabled = checkGate(featureGates.WEB_TIME_BASED_SETTINGS) && !isPickleballEvent;
    const isEventRegisterAsIndidualEnabled = checkGate(featureGates.EVENT_REGISTER_AS_INDIVIDUAL);
    const isOverrideDivisionRegDateEnabled = checkGate(featureGates.OVERRIDE_DIVISION_REG_DATES);
    const isManualCloseDivisionRegEnabled = checkGate(featureGates.MANUAL_CLOSE_DIVISION_REG);

    const surfaceTypeOptions = coreUI.types?.tms?.surfaceTypes;
    const environmentTypes = coreUI.types?.tms?.environmentTypes;
    const isFeedInConsolationFlagEnabled = checkGate(featureGates.FEED_IN_CONSOLATION_DRAWS);
    const drawElimTypes = getAllowedDrawEliminationTypes(drawEliminationTypes, isFeedInConsolationFlagEnabled);
    const timeZoneObj = coreUI?.types?.timeZones?.find(tz => { return tz.id === parseInt(values?.eventSchedule?.timeZone || DefaultTimeZone); });
    const divisionCategoryTypeOptions = coreUI.types?.divisionCategoryTypes;

    const showDelete = (eventDivisions, i) => {
        if (openDivisionIndex !== i) {
            return false;
        }
        if (editing) {
            if (eventDivisions.length > 1) {
                if (eventDivisions[i].registeredCount) {
                    return registeredPlayers <= 0;
                }
                return true;
            }
            return false;
        }
        return eventDivisions.length > 1;
    };

    const validateFee = (value, division) => {
        if (division?.registeredCount > 0) {
            return { error: null };
        }
        return validateMinEntryFee(
            value,
            isVerifiedEvent,
            minimumEntryFee,
            allowZeroEntryFee,
            minimumOrZeroEntryFeeErrorMessage,
            minimumEntryFeeErrorMessage,
        );
    };

    const scoreTypes = Object.values(scoringTypes);
    const tennisScoringTypes = scoreTypes.filter(type => type.sportTypeId === sportTypeIds.TENNIS);
    const scoreTypesForPickleball = Object.values(singlePointScoringTypes);
    const pickleballScoreTypes = scoreTypesForPickleball.slice(2, 16);

    const validateRegStartUtc = (value, regEndUtc) => {
        if (value && moment.utc(value)?.isBefore(values.eventSchedule.registrationStartUtc)) {
            return {
                error: 'Date cannot be before the event registration start date and time.',
            };
        }
        if (value && regEndUtc && moment.utc(value)?.isAfter(regEndUtc)) {
            return {
                error: 'Registration start cannot be after the division registration end date and time.',
            };
        }
        return null;
    };

    const validateRegEndUtc = (value, regStartUtc) => {
        if (value && moment.utc(value)?.isAfter(values.eventSchedule.registrationEndUtc)) {
            return {
                error: 'Date cannot be after the event registration end date and time.',
            };
        }
        if (value && regStartUtc && moment.utc(value)?.isBefore(regStartUtc)) {
            return {
                error: 'Registration end cannot be before the division registration start date and time.',
            };
        }
        return null;
    };

    const validateCancellationValue = (value, selfWithdrawEnabled) => {
        if (moment.utc(value)?.isAfter(values.eventSchedule.eventEndUtc)) {
            return {
                error: 'Date cannot exceed the event end date and time.',
            };
        }
        return validateCancellationDate(value, selfWithdrawEnabled);
    };

    const validateMaxRefundDateValue = (value, maxRefundEnabled) => {
        if (moment.utc(value)?.isAfter(values.eventSchedule.eventEndUtc)) {
            return {
                error: 'Date cannot exceed the event end date and time.',
            };
        }
        return validateMaxRefundDate(value, maxRefundEnabled);
    };

    const disableEntryFee = (division) => {
        const hasRegisteredPlayers = division.registeredCount > 0 && division.id;
        const paymentsBlocked = !paymentsEnabled && values.published && !allowOffPlatformPayments;
        const isUnpoweredPersonalClub = isPersonalClub(clubTierType) && !userIsPowered;
        const isOfficialHsEvent = clubIsHighSchool && values.hsEventType && values.hsEventType === EventHsTypes.OFFICIAL;

        return isDemoEvent || hasRegisteredPlayers || paymentsBlocked || isUnpoweredPersonalClub || isOfficialHsEvent;
    };

    const handleTimeZoneConversion = (utcDate) => {
        if (utcDate) {
            return moment.utc(utcDate).tz(timeZoneObj.value);
        }
        return null;
    };

    const validateSelfWithDraw = (val, i) => {
        if (val === false) {
            formApi.setValue(['eventDivisions', i, 'autoRefundEnabled'], false);
            formApi.setValue(['eventDivisions', i, 'maxAutoRefundDateUtc'], null);
            formApi.setError(['eventDivisions', i, 'maxAutoRefundDateUtc'], null);
            formApi.setValue(['eventDivisions', i, 'selfWithdrawAllowed'], false);
            formApi.setValue(['eventDivisions', i, 'maxSelfWithdrawDateUtc'], null);
            formApi.setError(['eventDivisions', i, 'maxSelfWithdrawDateUtc'], null);
        }
    };

    const handleSpecialDivisionChange = (val, i) => {
        if (val === false) {
            formApi.setValue(['eventDivisions', i, 'specificDivisionDateUtc'], null);
            formApi.setError(['eventDivisions', i, 'specificDivisionDateUtc'], null);
        }
        const getSpecificDivisionDateUtc = formApi.getValue(['eventDivisions', i, 'specificDivisionDateUtc']);
        if (val && !getSpecificDivisionDateUtc) {
            formApi.setTouched(['eventDivisions', i, 'specificDivisionDateUtc']);
            formApi.setError(['eventDivisions', i, 'specificDivisionDateUtc'], 'This field is required.');
        }
    };

    const validateSpecialDivisionDateValue = (value, specificDivisionDateEnabled, showDateError) => {
            if (moment.utc(value)?.isAfter(values.eventSchedule.eventEndUtc)) {
                return {
                    error: 'Date cannot exceed the event end date and time.',
                };
            }

        return validateSpecialDivisionDate(value, specificDivisionDateEnabled, showDateError);
    };

    const handleDivisionCategoryChange = (val, i) => {
        if (val === null) {
            formApi.setValue(['eventDivisions', i, 'divisionCategory'], null);
            formApi.setValue(['eventDivisions', i, 'divisionCategoryModel'], null);
        } else {
            formApi.setValue(['eventDivisions', i, 'divisionCategory'], val.id);
            formApi.setValue(['eventDivisions', i, 'divisionCategoryModel'], val);
        }
    };

    const handleDivisionEnvSurfaceChange = (val, f, i) => {
        formApi.setValue(['eventDivisions', i, f], (val || []));
    };

    const handleGenderChange = (val, i) => {
        const currentDivision = values[field][i];
        const isDivisionGenderFemale = genderTypes.find(gt => gt.value === divisionGenderTypes.FEMALE).id === parseInt(val);
        if (!isDivisionGenderFemale && currentDivision?.acceptance?.list?.type === acceptanceRankingValues.WTA) {
            formApi.setValue(['eventDivisions', i, 'acceptance', 'list', 'type'], acceptanceRankingValues.UTR);
            formApi.setValue(['eventDivisions', i, 'acceptance', 'list', 'rating'], acceptanceListRatingTypes.find(rt => rt.value === 'VERIFIED_SINGLES').id);
        }
    };

    const handleGameTypeChange = (val, i) => {
        const currentDivision = values[field][i];
        const isDivisionSingles = isSinglesFormat(parseInt(val));
        if (!isDivisionSingles && currentDivision?.acceptance?.type === acceptanceValues.LIST) {
            formApi.setValue(['eventDivisions', i, 'acceptance', 'type'], null);
        }
        if (isDivisionSingles && currentDivision?.allowTeamOnWaitlist) {
            formApi.setValue([field, i, 'allowTeamOnWaitlist'], false);
        }
        const hasEntryFee = isTemplate ? values?.template?.hasEntryFee : values?.hasEntryFee;
        if (hasEntryFee && !isDivisionSingles) {
            formApi.setValue(['eventDivisions', i, 'teamPaymentTypeId'], teamPaymentTypeIds.PAY_SEPARATELY);
        }
        const doublesOrDinglesIndex = getDoublesOrDinglesIndex(values.eventDivisions);
        if (doublesOrDinglesIndex !== -1 && i !== doublesOrDinglesIndex && !isDivisionSingles) {
            const teamPaymentTypeValue = values.eventDivisions[doublesOrDinglesIndex]?.teamPaymentTypeId;
            if (teamPaymentTypeValue) {
                formApi.setValue([field, i, 'teamPaymentTypeId'], teamPaymentTypeValue);
            }
        }
        const changePartnerAllowed = values?.changePartnerAllowed;
        if (changePartnerAllowed) {
            formApi.setValue(['eventDivisions', i, 'teamPaymentTypeId'], teamPaymentTypeIds.PAY_SEPARATELY);
        }
        if (isDivisionSingles) {
            formApi.setValue(['eventDivisions', i, 'teamPaymentTypeId'], null);
            formApi.setValue(['eventDivisions', i, 'registerWithoutPartner'], false);
            formApi.setError(['eventDivisions', i, 'teamPaymentTypeId'], null);
        }
        if (isDivisionSingles && parseInt(currentDivision?.gender) === eventGenderTypeIds.MIXED) {
            formApi.setValue(['eventDivisions', i, 'gender'], eventGenderTypeIds.ALL);
        }
        checkAndUpdateRegisterAsIndividual(val, currentDivision?.registerAsIndividual, i);
    };

    const checkAndUpdateRegisterAsIndividual = (divisionFormatType, registerAsIndividual, i) => {
        if ((parseInt(divisionFormatType) === divisionFormatTypeIds.SINGLES || parseInt(divisionFormatType) === divisionFormatTypeIds.DINGLES) && registerAsIndividual) {
            formApi.setValue(['eventDivisions', i, 'registerAsIndividual'], false);
        }
    };

    const minRatingSliderValue = isPickleballEvent ? minUtrpValue : minUtrValue;
    const maxRatingSliderValue = isPickleballEvent ? maxUtrpValue : maxUtrValue;

    const handleRatingRangeSlideStop = (e, i) => {
        const [minValue, maxValue] = e.target.value;

        if (minValue === minRatingSliderValue && maxValue === maxRatingSliderValue && values.eventDivisions[i].restrictByRating === true) {
            formApi.setValue(['eventDivisions', i, 'restrictByRating'], false);
        }
    };

    const handleAgeRangeSlideStop = (e, setThumbValues, i) => {
        const [minValue, maxValue] = e.target.value;
        const overMaxAgeSliderValue = Object.keys(eventDivisionAgeSliderDictionary).length - 1;

        if (minValue === 0 && minValue === maxValue) {
            e.target.value[1] = 1;
            setThumbValues(e.target.value);
        }

        if (maxValue === overMaxAgeSliderValue && minValue === maxValue) {
            e.target.value[0] = overMaxAgeSliderValue - 1;
            setThumbValues(e.target.value);
        }

        if (minValue === 0 && maxValue === overMaxAgeSliderValue && values.eventDivisions[i].restrictAge === true) {
            formApi.setValue(['eventDivisions', i, 'restrictAge'], false);
            formApi.setValue(['eventDivisions', i, 'restrictAgeByDateType'], null);
        }
    };

    const validateBallType = (value) => {
        if (value === null || value === undefined || value === '') {
            return {
                error: 'This field is required.',
            };
        }

        const existingEventTypeSelection = formApi.values.$verified;
        if (value && value !== ballTypeValues.YELLOW && existingEventTypeSelection === EventVerifiedTypes.UnVerified.toString()) {
            return {
                error: errorMessage.ballTypeValueErrorMessage,
            };
        }
    };

    const verifiedFieldPath = isTemplate === true ? ['template', 'verified'] : '$verified';

    const handleBallTypeChanges = (value, index) => {
        const eventDivisions = formApi.values.eventDivisions || [];
        const existingEventTypeSelection = formApi.values.$verified;
        const isUnverified = existingEventTypeSelection === EventVerifiedTypes.UnVerified.toString();
        const isYellow = value === ballTypeValues.YELLOW;

        if (value && !isYellow && isUnverified) {
            formApi.setError(verifiedFieldPath, errorMessage.eventTypeErrorMessage);
            return;
        }

        if (!value || (isYellow && isUnverified && eventDivisions.length > 0)) {
            const remaining = eventDivisions.filter((_div, i) => i !== index);
            const errorValue = checkRemainingDivisionsBallTypeValue(remaining, existingEventTypeSelection);
            if (errorValue !== undefined) {
                formApi.setError(verifiedFieldPath, errorValue);
            }
        }
    };

    const showTeamPaymentOption = (currentDivision) => {
        return isDinglesOrDoublesFormat(parseInt(currentDivision?.divisionFormatType));
    };

    const showRegisterAsIndividuals = (divisionFormatType, isEventRegisterAsIndidualEnabled) => {
        return isEventRegisterAsIndidualEnabled && parseInt(divisionFormatType) === divisionFormatTypeIds.DOUBLES;
    };

    const handleTeamPaymentOption = (val, index, formApi) => {
        const eventDivisions = formApi.values.eventDivisions || [];
        if (eventDivisions.length > 0) {
            eventDivisions.forEach((div, i) => {
                if (index !== i) {
                    if (isDinglesOrDoublesFormat(parseInt(div?.divisionFormatType))) {
                        formApi.setValue([field, i, 'teamPaymentTypeId'], val);
                    }
                }

                if (!val || parseInt(val) === teamPaymentTypeIds.PAY_AS_A_TEAM) {
                    if (div?.registerWithoutPartner === true) {
                        formApi.setValue([field, i, 'registerWithoutPartner'], false);
                    }
                    if (div?.allowTeamOnWaitlist === true) {
                        formApi.setValue([field, i, 'allowTeamOnWaitlist'], false);
                    }
                }
            });
        }
    };

    const validateRestrictAgeByDateTypeValue = (val, currentDivision) => {
        if (currentDivision.restrictAge && !val) {
            return {
                error: 'This field is required.',
            };
        }
    };

    const handleRestrictAgeChange = (val, i, eventDivisions) => {
        if (!val) {
            formApi.setValue(['eventDivisions', i, 'restrictAgeByDateType'], null);
            formApi.setError(['eventDivisions', i, 'restrictAgeByDateType'], null);
        } else {
            const ageRestrictedDivisionIndex = getAgeRestrictedDivisionIndex(eventDivisions);

            if (ageRestrictedDivisionIndex !== -1) {
                const defaultValue = eventDivisions[ageRestrictedDivisionIndex].restrictAgeByDateType;
                formApi.setValue(['eventDivisions', i, 'restrictAgeByDateType'], defaultValue);
            } else {
                formApi.setValue(['eventDivisions', i, 'restrictAgeByDateType'], RestrictAgeByDateTypeIds.EVENTSTARTDATE);
            }
        }
    };

    const handleRestrictAgeByDateType = (val, index, eventDivisions) => {
        if (val && eventDivisions?.length > 0) {
            eventDivisions.forEach((div, i) => {
                if (index !== i && div.restrictAge) {
                    formApi.setValue(['eventDivisions', i, 'restrictAgeByDateType'], val);
                }
            });
        }
    };

    const getTeamPaymentOptionDescription = (i, isTemplate, values) => {
        const hasEntryFee = isTemplate ? values?.template?.hasEntryFee : values?.hasEntryFee;
        if (hasEntryFee) {
            return 'Since player entry fees are added, pay separately is required.';
        }

        const doublesOrDinglesIndex = getDoublesOrDinglesIndex(values.eventDivisions);
        if (doublesOrDinglesIndex !== -1 && i !== doublesOrDinglesIndex) {
            return 'To modify this, adjust the first team payment selection above.';
        }
    };

    const isTeamPaymentOptionDisabled = (i, isTemplate, values, division) => {
        const changePartnerAllowed = values?.changePartnerAllowed;
        if (changePartnerAllowed) {
            // if change partner is allowed, only Pay Separately is allowed for doubles divisions
            return true;
        }

        const hasEntryFee = isTemplate ? values?.template?.hasEntryFee : values?.hasEntryFee;
        if (hasEntryFee) {
            return true;
        }

        const hasRegisterAsIndividual = values.eventDivisions.findIndex(div => div.registerAsIndividual);
        if (hasRegisterAsIndividual !== -1) {
            return true;
        }

        if (!!division.id && division.registeredCount > 0) {
            return true;
        }
        const doublesOrDinglesIndex = getDoublesOrDinglesIndex(values.eventDivisions);

        return doublesOrDinglesIndex !== -1 && i !== doublesOrDinglesIndex;
    };

    const validateTeamPaymentValue = (val, currentDivision) => {
        const isDinglesOrDoublesDivision = isDinglesOrDoublesFormat(parseInt(currentDivision?.divisionFormatType));
        if (!val && isDinglesOrDoublesDivision) {
            return {
                error: 'This field is required.',
            };
        }
    };

    const showRegisterWithoutPartnerOption = (currentDivision) => {
        return !!(parseInt(currentDivision?.teamPaymentTypeId) === teamPaymentTypeIds.PAY_SEPARATELY && !currentDivision?.registerAsIndividual && parseInt(currentDivision?.divisionFormatType) !== divisionFormatTypeIds.SINGLES);
    };

    const defaultDivisionValue = (formApi, currency, sportTypeId, isTemplate) => {
        const eventDivisions = formApi.values.eventDivisions || [];
        const doublesOrDinglesIndex = getDoublesOrDinglesIndex(eventDivisions);
        const defaultDivision = getDefaultDivision(formApi.values, currency.id, null, sportTypeId);
        const isDinglesOrDoublesDivision = isDinglesOrDoublesFormat(parseInt(defaultDivision.divisionFormatType));
        let teamPaymentTypeValue;
        if (isDinglesOrDoublesDivision && doublesOrDinglesIndex !== -1) {
            teamPaymentTypeValue = eventDivisions[doublesOrDinglesIndex]?.teamPaymentTypeId;
        }
        const hasEntryFee = isTemplate ? formApi.values?.template?.hasEntryFee : formApi.values?.hasEntryFee;
        if (isDinglesOrDoublesDivision && hasEntryFee) {
            teamPaymentTypeValue = teamPaymentTypeIds.PAY_SEPARATELY;
        }
        if (formApi.values?.changePartnerAllowed) {
            // if change partner is allowed, only Pay Separately is allowed for doubles divisions
            teamPaymentTypeValue = teamPaymentTypeIds.PAY_SEPARATELY;
        }
        return {
            ...defaultDivision,
            teamPaymentTypeId: teamPaymentTypeValue,
        };
    };

    const getGenderTypes = (currentDivision, coreUI) => {
        const isDinglesOrDoublesDivision = isDinglesOrDoublesFormat(parseInt(currentDivision?.divisionFormatType));
        if (isDinglesOrDoublesDivision) {
            return getAllowedGenderTypes(coreUI, true);
        }
        return genderTypes;
    };

    const getTitle = (name, i) => {
        return `${i + 1}. ${name || 'New Division'}`;
    };

    const handleCardClick = (i) => {
        if (i !== openDivisionIndex) {
            setOpenDivisionIndex(i);
            setTimeout(() => {
                if (cardRefs.current[i]) {
                    cardRefs.current[i].style.scrollMargin = isMobileWidth ? '74px' : '90px';
                    cardRefs.current[i].scrollIntoView({ behavior: 'smooth' });
                }
            }, 100);
        }
    };
    const handleTitleClick = (i) => {
        if (i === openDivisionIndex) {
            setOpenDivisionIndex();
        }
    };

    const handleAddNewDivision = (e) => {
        e.preventDefault();
        const blankNameIndices = (values[field] || []).reduce((acc, div, i) => {
            if (!div.name || div.name.trim() === '') {
                acc.push(i);
            }
            return acc;
        }, []);
        if (blankNameIndices.length > 0) {
            blankNameIndices.forEach(i => formApi.setTouched([field, i, 'name'], true));
            return;
        }
        formApi.addValue(field, defaultDivisionValue(formApi, currency, sportTypeId, isTemplate));
        setOpenDivisionIndex(values[field].length);
        setTimeout(() => {
            if (cardRefs.current[values[field].length]) {
                cardRefs.current[values[field].length].style.scrollMargin = isMobileWidth ? '74px' : '90px';
                cardRefs.current[values[field].length].scrollIntoView({ behavior: 'smooth' });
            }
        }, 100);
    };

    const handleDeleteClick = (i, e) => {
        e.stopPropagation();
        setDeleteConfirmation(i);
    };

    const deleteDivision = (index) => {
        formApi.removeValue(field, index);
        setDeleteConfirmation(null);
    };

    const closeDivisionRegistration = (index, doNotShowAgain) => {
        if (editing && doNotShowAgain) {
            localStorage.setItem(generateLocalStorageKey(DoNotShowCloseRegistrationPopup, formApi.values.id, auth), true);
        }

        formApi.setValue(['eventDivisions', index, 'closeRegistration'], true);
        formApi.setValue(['eventDivisions', index, 'overrideRegDates'], false);
        formApi.reset(['eventDivisions', index, 'registrationStartUtc']);
        formApi.reset(['eventDivisions', index, 'registrationEndUtc']);
        setCloseRegistrationConfirmation(null);
    };

    const ratingRangeHoverText = (
        <ul>
            <li>
                If you select a specific {isPickleballEvent ? ratingTypes.pickleball : ratingTypes.tennis} range, it&apos;s recommended to include this range in the division name.
            </li>
            <li>
                To restrict players outside the selected rating range from registering for a division, enable the “Restrict Rating” option below.
            </li>
            <li>
                When &apos;Restrict Rating&apos; is enabled, rating restrictions apply only to players with a numeric {isPickleballEvent ? ratingTypes.pickleball : ratingTypes.tennis} rating for the divisions game type (e.g. singles or doubles). Players with {isPickleballEvent ? 'only an estimated rating' : 'a historical rating, estimated rating,'} or unrated can still register for a division, even if &apos;Restrict Rating&apos; is enabled.
            </li>
            <li>
                As example, a player with a singles {isPickleballEvent ? ratingTypes.pickleball : ratingTypes.tennis} rating but no doubles {isPickleballEvent ? ratingTypes.pickleball : ratingTypes.tennis} rating will be restricted only in singles divisions with &apos;Restrict Rating&apos; enabled. This player won&apos;t face any restrictions when registering for doubles divisions.
            </li>
            <li>
                When “Restrict Rating” is off, this acts as purely informational and all players will still be able to register.
            </li>
        </ul>
    );

    const isRestrictAgeByDateTypeDisabled = (eventDivisions, i) => {
        if (values.eventDivisions[i].restrictAge) {
            const ageRestrictedDivisionIndex = getAgeRestrictedDivisionIndex(eventDivisions);
            return ageRestrictedDivisionIndex !== -1 && i !== ageRestrictedDivisionIndex;
        }

        return true;
    };

    const handleCloseRegistrationChange = (val, i) => {
        const preferenceForNotShowingCloseRegistrationConfirmationPopup = localStorage.getItem(generateLocalStorageKey(DoNotShowCloseRegistrationPopup, formApi.values.id, auth));
        const division = values.eventDivisions[i];
        if (val) {
            if (preferenceForNotShowingCloseRegistrationConfirmationPopup === 'true' || division.acceptance?.type !== acceptanceValues.WAIT_LIST) {
                closeDivisionRegistration(i, false);
            } else {
                setCloseRegistrationConfirmation(i);
            }
        } else {
            formApi.setValue(['eventDivisions', i, 'closeRegistration'], false);
        }
    };

    const handleOverrideRegDatesChange = (val, i) => {
        formApi.setValue(['eventDivisions', i, 'overrideRegDates'], val);
        if (val) {
            formApi.setValue(['eventDivisions', i, 'registrationStartUtc'], values?.eventSchedule?.registrationStartUtc);
            formApi.setValue(['eventDivisions', i, 'registrationEndUtc'], values?.eventSchedule?.registrationEndUtc);
        } else {
            formApi.reset(['eventDivisions', i, 'registrationStartUtc']);
            formApi.reset(['eventDivisions', i, 'registrationEndUtc']);
        }
    };

    const handleOverrideSelfCancelChange = (val, i) => {
        formApi.setValue(['eventDivisions', i, 'overrideSelfCancelSettings'], val);
        if (val) {
            if (values.selfWithdrawAllowed && values.maxSelfWithdrawDateUtc) {
                formApi.setValue(['eventDivisions', i, 'selfWithdrawAllowed'], true);
                formApi.setValue(['eventDivisions', i, 'maxSelfWithdrawDateUtc'], values.maxSelfWithdrawDateUtc);
            }
            if (values.autoRefundEnabled && values.maxAutoRefundDateUtc && values.eventDivisions[i].entryFee) {
                formApi.setValue(['eventDivisions', i, 'autoRefundEnabled'], true);
                formApi.setValue(['eventDivisions', i, 'maxAutoRefundDateUtc'], values.maxAutoRefundDateUtc);
            }
        } else {
            formApi.setValue(['eventDivisions', i, 'selfWithdrawAllowed'], false);
            formApi.reset(['eventDivisions', i, 'maxSelfWithdrawDateUtc']);
            if (values.eventDivisions[i].autoRefundEnabled) {
                formApi.setValue(['eventDivisions', i, 'autoRefundEnabled'], false);
                formApi.reset(['eventDivisions', i, 'maxAutoRefundDateUtc']);
            }
        }
    };

    const handleMaxSelfWithdrawDateChange = (val, i) => {
        formApi.setValue(['eventDivisions', i, 'maxSelfWithdrawDateUtc'], val);
        if (values.autoRefundEnabled && moment(values.eventDivisions[i].maxAutoRefundDateUtc).tz(timeZoneObj.value).isAfter(val)) {
            formApi.setValue(['eventDivisions', i, 'maxAutoRefundDateUtc'], val);
        }
    };

    const handleRegisterAsIndividuals = (val, index, formApi) => {
        const eventDivisions = formApi.values.eventDivisions || [];
        if (val && eventDivisions?.length > 0) {
            eventDivisions.forEach((div, i) => {
                if (isDinglesOrDoublesFormat(parseInt(div?.divisionFormatType))) {
                    formApi.setValue([field, i, 'teamPaymentTypeId'], teamPaymentTypeIds.PAY_SEPARATELY);
                }
                if (i === index && div?.registerWithoutPartner === true) {
                    formApi.setValue([field, i, 'registerWithoutPartner'], false);
                }
            });
        }
    };

    const disableRegisterAsIndividuals = (values) => {
        const hasPlayerRegisteredWithTeamPayment = values.eventDivisions.findIndex(div => div.registeredCount > 0 && div.divisionFormatType !== divisionFormatTypeIds.SINGLES && div.teamPaymentTypeId === teamPaymentTypeIds.PAY_AS_A_TEAM);
        return hasPlayerRegisteredWithTeamPayment !== -1;
    };

    const divisionFormatTypeHoverText = () => {
        const hasDinglesTypeOption = formatTypes.some(formatType => formatType.id === divisionFormatTypeIds.DINGLES);
        return (
            <>
                <div>
                    For doubles {hasDinglesTypeOption ? 'or dingles' : ''}, providers can choose whether partners pay separately or as a team, depending on the selected team payment option.
                </div>
                {hasDinglesTypeOption && (
                    <div className="mt16">
                        Dingles results do not affect ratings.
                    </div>
                )}
            </>
        );
    };

    const renderSelfCancelOverrideContent = (i) => {
        if (!values.eventDivisions[i].overrideSelfCancelSettings) {
            return null;
        }
        return (
            <>
                {values.selfWithdrawAllowed && (
                    <div className={`row`}>
                        <div className={`col-5 d-flex flex-column justify-content-center`}>
                            <CheckBox
                                id={`selfWithdrawAllowed`}
                                field={['eventDivisions', i, 'selfWithdrawAllowed']}
                                customLabel="Allow players to self-withdraw registration"
                                formGroupCssClass="align-self-center"
                                hoverText="If enabled, participants will be able to withdraw their registration without having to contact the tournament director. You will be able to set the final date to allow players to withdraw their registration on their own and decide whether or not they are initiated a refund."
                                onChange={val => validateSelfWithDraw(val, i)}
                                disableLabelSelect
                                data-testid="event-edit.allow-self-withdraw"
                            />
                        </div>
                        {!isTemplate && (
                            <div className={`col-7`}>
                                <DateTimePicker
                                    className="form-control"
                                    label={'Max withdraw date'}
                                    selected={handleTimeZoneConversion(values.eventDivisions[i].maxSelfWithdrawDateUtc)}
                                    minDate={moment(values.eventSchedule.registrationStartUtc).tz(timeZoneObj.value)}
                                    maxDate={moment(values.eventSchedule.eventEndUtc).tz(timeZoneObj.value)}
                                    disabled={values.eventDivisions[i].selfWithdrawAllowed !== true}
                                    onChange={(val) => handleMaxSelfWithdrawDateChange(val, i)}
                                    field={['eventDivisions', i, 'maxSelfWithdrawDateUtc']}
                                    validate={value => validateCancellationValue(value, values.eventDivisions[i].selfWithdrawAllowed)}
                                />
                            </div>
                        )}
                    </div>
                )}
                {values.autoRefundEnabled && (
                    <div className={`row`}>
                        <div className={`col-5 d-flex flex-column justify-content-center`}>
                            <CheckBox
                                id={`autoRefundEnabled`}
                                field={['eventDivisions', i, 'autoRefundEnabled']}
                                customLabel="Refund self-withdrawals automatically"
                                formGroupCssClass="align-self-center"
                                disabled={values.eventDivisions[i].entryFee === null || values.eventDivisions[i].entryFee < (minimumEntryFee || 1)}
                                hoverText="If enabled, participants will be automatically refunded when withdrawing their registration."
                                disableLabelSelect
                            />
                        </div>
                        {!isTemplate && (
                            <div className={`col-7`}>
                                <DateTimePicker
                                    className="form-control"
                                    label={'Max auto-refund date'}
                                    minDate={moment(values.eventSchedule.registrationStartUtc).tz(timeZoneObj.value)}
                                    selected={handleTimeZoneConversion(values.eventDivisions[i].maxAutoRefundDateUtc)}
                                    maxDate={moment(values.eventDivisions[i].maxSelfWithdrawDateUtc).tz(timeZoneObj.value)}
                                    disabled={values.eventDivisions[i].autoRefundEnabled !== true}
                                    validate={value => validateMaxRefundDateValue(value, values.eventDivisions[i].autoRefundEnabled)}
                                    field={['eventDivisions', i, 'maxAutoRefundDateUtc']}
                                />
                            </div>
                        )}
                    </div>
                )}
            </>
        );
    };

    const entryFeeHoverText = isVerifiedEvent ? (
        <ul>
            {(countryCode !== 'AU' || clubTierType.id !== 5) && (
                <li>
                    UTR Sports charges a {sportTypeId === sportTypeIds.PICKLEBALL ? `transaction` : `verified`} fee for non powered subscribers. For Power subscribers, this fee is waived. For fee amounts, please <a href={links.stripeFeeArticle} target="_blank" rel="noopener noreferrer">click here</a>
                </li>
            )}
            {paymentProviderSetupType === 2 && (
                <li>
                    A Stripe processing fee will be assessed on each division fee. Stripe fees will vary by country and account. Contact Stripe if you have any questions.
                </li>
            )}
            <li>
                Division fees cannot be changed once the event has registrants.
            </li>
            {allowZeroEntryFee && (
                <li>
                    {`Division fees should be 0 or left blank for free divisions or at least ${formattedMinimumEntryFee} for paid divisions`}
                </li>
            )}
            {!allowZeroEntryFee && (
                <li>
                    {`Division fees must be at least ${formattedMinimumEntryFee} for each division`}
                </li>
            )}
        </ul>
    ) : (
        <ul>
            {paymentProviderSetupType !== 2 && (
                <li>
                    All players will pay a standard pay processing charge if there is an division fee.
                </li>
            )}
            {paymentProviderSetupType === 2 && (
                <li>
                    A Stripe processing fee will be assessed on each division fee. Stripe fees will vary by country and account. Contact Stripe if you have any questions.
                </li>
            )}
            <li>
                Division fees cannot be changed once the event has registrants.
            </li>
            <li>
                {`Division fees should be 0 or left blank for free divisions or at least ${formattedMinimumEntryFee} for paid divisions`}
            </li>
        </ul>
    );

    return (
        <div className={`col-12`}>
            {values[field]?.map((division, i) => {
                const isOpenDivision = i === openDivisionIndex;
                return (
                    <Tip
                        key={`division-${division.id || i}`}
                        variant="info"
                        showTip={!isTemplate && i === 0}
                        valign="top"
                        dangerouslySetInnerHTML={true}
                        content={`<b>Tip:</b> Name divisions as broadly as possible (e.g. "Men's Level-Based Singles" or "Coed Level-Based Singles"). Once players register, you can move players across any number of draws in order to facilitate level-based play.`}
                    >

                        <div className={`form-block mb32 ${isOpenDivision ? '' : 'clickable'}`} onClick={() => handleCardClick(i)} ref={ref => { cardRefs.current[i] = ref; }} data-testid={`event-edit.divisions.card-${i}`}>
                            <div className={`form-block-title ${isOpenDivision ? 'clickable' : 'bb0 p0'}`} onClick={() => handleTitleClick(i)} data-testid={`event-edit.divisions.title-${i}`}>
                                <span className="text-small text-medium">
                                    {getTitle(division.name, i)}
                                </span>
                                <span>
                                    {showDelete(values[field], i) && (
                                        <span
                                            className="close-icon mr16"
                                            onClick={(e) => handleDeleteClick(i, e)}
                                            data-testid={`event-edit.divisions.delete-btn-${i}`}
                                        >
                                            <i className="fal fa-trash-alt" />
                                        </span>
                                    )}
                                    <span className="close-icon">
                                        <i className={`uts uts-${isOpenDivision ? 'collapse' : 'expand'}-arrow fs12`} />
                                    </span>
                                </span>
                            </div>
                            {i === openDivisionIndex && (
                                <>
                                    <div className="form-block-content">
                                        <div className="row">
                                            {!isFlexLeagueEvent && (
                                                <div className="col-md-6">
                                                    <Select
                                                        id={`divisions${i}elimination`}
                                                        label="Draw Type"
                                                        hoverText={(
                                                            <ul>
                                                                <li>
                                                                    <strong>First Match Backdraw:</strong> Players move to the backdraw if they lose their first match (including in the second round if they receive a bye in the first round) and are thus guaranteed two matches.<a href={links.firstMatchBackDrawArticle} target="_blank" rel="noopener noreferrer">Learn more</a>
                                                                </li>
                                                                <li>
                                                                    <strong>Single Elimination:</strong> Players are eliminated from the tournament once they lose and are thus guaranteed one match.<a href={links.singleEliminationArticle} target="_blank" rel="noopener noreferrer">Learn more</a>
                                                                </li>
                                                                <li>
                                                                    <strong>Round Robin:</strong> Players in a draw play a series of matches versus all other players grouped in the draw. Round Robins are best with a group of 4-6 players of similar skill to guarantee at least three matches.<a href={links.roundRobinArticle} target="_blank" rel="noopener noreferrer">Learn more</a>
                                                                </li>
                                                                <li>
                                                                    <strong>Compass Draw:</strong> Players move through a series of different draws depending on whether they win or not and are usually guaranteed three matches. Compass draws are sized for either 8 or 16 players.<a href={links.compassDrawArticle} target="_blank" rel="noopener noreferrer">Learn more</a>
                                                                </li>
                                                                <li>
                                                                    <strong>Match Play:</strong> Less structured and flexible formats such as ad-hoc matches, challenge matches, etc.<a href={links.matchPlayArticle} target="_blank" rel="noopener noreferrer">Learn more</a>
                                                                </li>
                                                                <li>
                                                                    <strong>Double Elimination:</strong> Players must lose twice to be eliminated. After their first loss in the main draw, they enter the back draw. The final player in the back draw competes with the main draw winner for first place.<a href={links.doubleEliminationArticle} target="_blank" rel="noopener noreferrer">Learn more</a>
                                                                </li>
                                                                <li>
                                                                    <strong>Feed-in Consolation:</strong> Players will move to a consolation draw after their first loss up until either the round of 16 or quarterfinals, as decided by the organizer. There is also the option to auto-create up to two playoff draws for players who lose in the main draw after the designated feed-in cutoff round.<a href={links.feedInConsolationArticle} target="_blank" rel="noopener noreferrer">Learn more</a>
                                                                </li>
                                                                <li>
                                                                    <strong>Ad-Hoc:</strong> Similar to a round-robin draw but let’s the organizer decide the number of rounds each draw will have. For instance, 8 players can compete in a round robin style draw which concludes in just 4 rounds.<a href={links.adhocArticle} target="_blank" rel="noopener noreferrer">Learn more</a>
                                                                </li>
                                                            </ul>
                                                        )}
                                                        field={[field, i, 'eliminationType']}
                                                        options={drawElimTypes}
                                                        validate={required}
                                                    />
                                                </div>
                                            )}
                                            <div className={`${isFlexLeagueEvent ? 'col-md-12' : 'col-md-6'}`}>
                                                <Select
                                                    id={`divisions${i}format`}
                                                    label="Game Type"
                                                    hoverText={divisionFormatTypeHoverText()}
                                                    field={[field, i, 'divisionFormatType']}
                                                    options={formatTypes}
                                                    validate={required}
                                                    disabled={(division.id && division.registeredCount > 0)}
                                                    onChange={val => handleGameTypeChange(val, i)}
                                                />
                                            </div>
                                            <div className="col-md-12">
                                                <Input
                                                    label="Division name"
                                                    hoverText="We recommend allowing a range of different levels for registration. Once registration closes, you can group players from each division into draws based on player's ratings."
                                                    id="name"
                                                    field={[field, i, 'name']}
                                                    placeholder="Ex. Co-ed Singles Open"
                                                    validate={required}
                                                />
                                            </div>
                                            {showRegisterAsIndividuals(values.eventDivisions[i].divisionFormatType, isEventRegisterAsIndidualEnabled) && (
                                                <div className="col-md-12">
                                                    <ToggleSwitch
                                                        id={`divisions${i}registerAsIndividual`}
                                                        label="Only allow players to register as individuals"
                                                        field={[field, i, 'registerAsIndividual']}
                                                        reverse={false}
                                                        onChange={val => handleRegisterAsIndividuals(val, i, formApi)}
                                                        disabled={disableRegisterAsIndividuals(values)}
                                                    />
                                                </div>
                                            )}
                                            {showTeamPaymentOption(values.eventDivisions[i]) && (
                                                <div className="col-md-12">
                                                    <Select
                                                        id={`divisions${i}teamPaymentTypeId`}
                                                        label="Team Payment Option"
                                                        hoverText={(
                                                            <ul>
                                                                <li>This selection determines whether doubles partners pay as a team or pay separately for registration fees.</li>
                                                                <li>If ‘Pay as a team’ is chosen, only one partner will be required to register/pay the set team division fee.</li>
                                                                <li>If ‘Pay separately’ is chosen, both partners must pay their respective fees for the division. After the first partner registers and pays, the second must also register and pay their division/entry fees.</li>
                                                            </ul>
                                                        )}
                                                        description={getTeamPaymentOptionDescription(i, isTemplate, values)}
                                                        emptyOption="Select a payment option"
                                                        field={[field, i, 'teamPaymentTypeId']}
                                                        options={teamPaymentTypes}
                                                        onChange={val => handleTeamPaymentOption(val, i, formApi)}
                                                        validate={val => validateTeamPaymentValue(val, values.eventDivisions[i])}
                                                        disabled={isTeamPaymentOptionDisabled(i, isTemplate, values, division)}
                                                    />
                                                </div>
                                            )}
                                            {showRegisterWithoutPartnerOption(values.eventDivisions[i]) && (
                                                <div className="col-md-12">
                                                    <ToggleSwitch
                                                        id={`divisions${i}registerWithoutPartner`}
                                                        label="Allow players to register without a partner?"
                                                        field={[field, i, 'registerWithoutPartner']}
                                                        reverse={false}
                                                        hoverText={(
                                                            <ul>
                                                                <li>When enabled, players can sign up for a doubles division without specifying a partner.</li>
                                                                <li>During registration, players will see an ‘I need a partner’ choice. If they register this way, they will then appear on a need partner player list section in that division and can later be paired with other players who also need a partner.</li>
                                                                <li>This feature is exclusive to divisions/events where the ‘Pay separately’ payment option is used.</li>
                                                            </ul>
                                                        )}
                                                        disabled={(!!division.id && division.registeredCount > 0)}
                                                    />
                                                </div>
                                            )}
                                            <div className={`col-md-6`}>
                                                <Select
                                                    id={`divisions${i}gender`}
                                                    label="Gender"
                                                    hoverText={(
                                                        <ul>
                                                            <li>Choosing male or female will restrict registration to that gender.</li>
                                                            <li>Choosing co-ed will not restrict genders.</li>
                                                            <li>Choosing mixed (doubles only) will restrict partner selection to the opposite gender only.</li>
                                                        </ul>
                                                    )}
                                                    field={[field, i, 'gender']}
                                                    options={getGenderTypes(values.eventDivisions[i], coreUI)}
                                                    validate={required}
                                                    onChange={val => handleGenderChange(val, i)}
                                                />
                                            </div>
                                            <div className={`col-md-6`}>
                                                <Tip
                                                    variant="info"
                                                    showTip={i === 0 && showTip}
                                                    customStyle={{
                                                        left: 'calc(100% + 24px)',
                                                    }}
                                                    content="Division fees cannot be changed once a player registers for your event."
                                                >
                                                    <Input id={`divisions${i}currencyId`} field={[field, i, 'currencyId']} type="hidden" />
                                                    <InputGroupPre
                                                        id={`divisions${i}fee`}
                                                        label="Division Fee"
                                                        format="decimal"
                                                        onChange={(value, ev) => {
                                                            if (i === 0) {
                                                                if (onChange) {
                                                                    onChange(value, ev);
                                                                }
                                                            }

                                                            if (value && parseFloat(value) > 0) {
                                                                if (allowOffPlatformPayments) {
                                                                    formApi.setValue([field, i, 'paymentType'], 3);
                                                                } else {
                                                                    formApi.setValue([field, i, 'paymentType'], paymentProviderSetupType);
                                                                }
                                                            } else {
                                                                formApi.setValue([field, i, 'paymentType'], null);
                                                                formApi.setValue(['eventDivisions', i, 'autoRefundEnabled'], false);
                                                                formApi.setValue(['eventDivisions', i, 'maxAutoRefundDateUtc'], null);
                                                            }
                                                        }}
                                                        hoverText={
                                                            allowOffPlatformPayments ? (
                                                                <ul>
                                                                    <li>Organizer is responsible for collecting all division fees and refunding players when appropriate.</li>
                                                                    <li>Division fees cannot be changed once the event has registrants.</li>
                                                                    <li>Leave blank for free divisions.</li>
                                                                    <li>Division fee must be at least {formattedMinimumEntryFee} for each division.</li>
                                                                </ul>
                                                            ) : entryFeeHoverText
                                                        }
                                                        field={[field, i, 'entryFee']}
                                                        type="number"
                                                        pre={getEventCurrencySymbol(allowOffPlatformPayments, currency)}
                                                        placeholder="Ex. 45"
                                                        disabled={disableEntryFee(division)}
                                                        validate={value => validateFee(value, division)}
                                                        className="hide-spinner"
                                                        min={minimumEntryFee || null}
                                                    />
                                                </Tip>
                                            </div>
                                            {(allowOffPlatformPayments) && (
                                                <div className={`col-md-12 mb24 text-error`}>
                                                    * Fees will be collected directly by club
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    {!isPickleballEvent && (
                                        <div className="row">
                                            <div className={`col-md-6`}>
                                                <Select
                                                    id={`divisions${i}ballTypeValue`}
                                                    label="Ball Type"
                                                    field={[field, i, 'ballTypeValue']}
                                                    options={ballTypes}
                                                    validate={value => validateBallType(value)}
                                                    onChange={value => handleBallTypeChanges(value, i)}
                                                    attributeToSave={'value'}
                                                />
                                            </div>
                                        </div>
                                    )}
                                    <div className={`row`}>
                                        <div className={`col-5 d-flex flex-column justify-content-center`}>
                                            <CheckBox
                                                id={`specificDivisionDateEnabled`}
                                                field={['eventDivisions', i, 'specificDivisionDateEnabled']}
                                                customLabel={`Add a specific date to this division?`}
                                                formGroupCssClass="align-self-center"
                                                hoverText={(
                                                    <ul>
                                                        <li>If selected, players will see the specific date added on the division cards and during checkout.</li>
                                                        <li>Only one day/date may be selected and the date selected must be between the event start and end date as set in step 2 of events set up.</li>
                                                    </ul>
                                                )}
                                                onChange={val => handleSpecialDivisionChange(val, i)}
                                                disableLabelSelect
                                            />
                                        </div>
                                        {!isTemplate && (
                                            <div className={`col-7`}>
                                                <DateTimePicker
                                                    className="form-control"
                                                    label={'Division date'}
                                                    selected={handleTimeZoneConversion(values.eventDivisions[i].specificDivisionDateUtc)}
                                                    minDate={moment.utc(values.eventSchedule.eventStartUtc).tz(timeZoneObj.value)}
                                                    maxDate={moment.utc(values.eventSchedule.eventEndUtc).tz(timeZoneObj.value)}
                                                    disabled={values.eventDivisions[i].specificDivisionDateEnabled !== true}
                                                    onChange={(event) => {
                                                        formApi.setValue(['eventDivisions', i, 'specificDivisionDateUtc'], event);
                                                        if (!showDateError) {
                                                            formApi.setError(false);
                                                        }
                                                    }}
                                                    field={['eventDivisions', i, 'specificDivisionDateUtc']}
                                                    validate={value => validateSpecialDivisionDateValue(value, values.eventDivisions[i].specificDivisionDateEnabled, showDateError)}
                                                    hideTimePicker={true}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <div className="">
                                        {isManualCloseDivisionRegEnabled && (
                                            <div className={`form-block-divider is-open clickable`}>
                                                <CheckBox
                                                    id={`closeRegistration`}
                                                    field={['eventDivisions', i, 'closeRegistration']}
                                                    customLabel="Close registration for this division"
                                                    formGroupCssClass="align-self-center mb16"
                                                    hoverText={(
                                                        <>
                                                            <li><strong>Instantly closes registration</strong> for this division, overriding any set end dates.</li>
                                                            <li>New players won’t be able to register.</li>
                                                            <li>For <strong>waitlist divisions</strong>, closing registration <strong>freezes automatic waitlist logic</strong>. All future waitlist-to-main-position moves require <strong>manual adjustments</strong> by the provider (only accessible when closed). You’ll see an <strong>indication of this frozen logic on the Divisions tab and Player Division list</strong> on the event profile.</li>
                                                            <li>You can reopen registration, but the waitlist logic will stay frozen. Any new registrations will go to the waitlist, requiring continued manual management.</li>
                                                        </>
                                                    )}
                                                    onChange={val => handleCloseRegistrationChange(val, i)}
                                                    disableLabelSelect
                                                />
                                            </div>
                                        )}
                                        {isOverrideDivisionRegDateEnabled && (
                                            <>
                                                <div className={`form-block-divider is-open clickable`}>
                                                    <CheckBox
                                                        id={`overrideRegDates`}
                                                        field={['eventDivisions', i, 'overrideRegDates']}
                                                        customLabel={`Override event level registration dates`}
                                                        formGroupCssClass="align-self-center mb16"
                                                        onChange={val => handleOverrideRegDatesChange(val, i)}
                                                        disableLabelSelect
                                                        disabled={values.eventDivisions[i].closeRegistration === true}
                                                    />
                                                </div>
                                                {values.eventDivisions[i].overrideRegDates && (
                                                    <div className="row mb16">
                                                        <div className={`col-md-6`}>
                                                            <label className="text-dark mt16">Registration starts</label>
                                                            <div className="mt8">
                                                                <DateTimePicker
                                                                    label="Registration starts"
                                                                    id="regDateStart"
                                                                    field="registrationStartUtc"
                                                                    placeholderText="Select Date"
                                                                    selected={handleTimeZoneConversion(values.eventDivisions[i].registrationStartUtc)}
                                                                    minDate={moment(values.eventSchedule.registrationStartUtc).tz(timeZoneObj.value)}
                                                                    maxDate={moment(values.eventSchedule.registrationEndUtc).tz(timeZoneObj.value)}
                                                                    handleDateChange={(date) => {
                                                                        formApi.setValue(['eventDivisions', i, 'registrationStartUtc'], moment(date).tz(timeZoneObj.value, true));
                                                                    }}
                                                                    validate={value => validateRegStartUtc(value, values.eventDivisions[i].registrationEndUtc)}
                                                                    themeV3
                                                                    isStacked
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className={`col-md-6`}>
                                                            <label className="text-dark mt16">Registration ends</label>
                                                            <div className="mt8">
                                                                <DateTimePicker
                                                                    label="Registration ends"
                                                                    id="regDateEnd"
                                                                    field="registrationEndUtc"
                                                                    placeholderText="Select Date"
                                                                    selected={handleTimeZoneConversion(values.eventDivisions[i].registrationEndUtc)}
                                                                    minDate={moment(values.eventSchedule.registrationStartUtc).tz(timeZoneObj.value)}
                                                                    maxDate={moment(values.eventSchedule.registrationEndUtc).tz(timeZoneObj.value)}
                                                                    handleDateChange={(date) => {
                                                                        formApi.setValue(['eventDivisions', i, 'registrationEndUtc'], moment(date).tz(timeZoneObj.value, true));
                                                                    }}
                                                                    validate={value => validateRegEndUtc(value, values.eventDivisions[i].registrationStartUtc)}
                                                                    themeV3
                                                                    isStacked
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                    {values.preRegAndEligibleListEnabled && (
                                        <div className={`form-block-divider is-open clickable`}>
                                            <CheckBox
                                                id={`excludeFromEligibleListRestrictions`}
                                                field={['eventDivisions', i, 'excludeFromEligibleListRestrictions']}
                                                customLabel={`Exclude division from eligibility requirements`}
                                                formGroupCssClass="align-self-center mb16"
                                                disableLabelSelect
                                            />
                                        </div>
                                    )}
                                    {values.selfWithdrawAllowed && (
                                        <div className="">
                                            <div className={`form-block-divider is-open clickable`}>
                                                <CheckBox
                                                    id={`overrideSelfCancelSettings`}
                                                    field={['eventDivisions', i, 'overrideSelfCancelSettings']}
                                                    customLabel={`Override event level withdrawal settings`}
                                                    formGroupCssClass="align-self-center"
                                                    onChange={val => handleOverrideSelfCancelChange(val, i)}
                                                    disableLabelSelect
                                                />
                                            </div>
                                            {renderSelfCancelOverrideContent(i)}
                                        </div>
                                    )}
                                    <div
                                        className={`form-block-divider is-open clickable`}
                                        data-testid="event-edit.divisions.options-toggle"
                                    >
                                        <div className="title">
                                            <div className="text-small text-uppercase text-medium">Options</div>
                                            <small style={{ textTransform: 'unset' }}>
                                                Scoring, age range, rating range...
                                            </small>
                                        </div>
                                    </div>
                                    <div className={`form-block-content`}>
                                        <div className="row">
                                            <div className="col-md-6">
                                                <Select
                                                    id={`divisions${i}scoring`}
                                                    label="Scoring Format"
                                                    hoverText={`This is informational for the players and you can adjust the scoring format if needed in the future.${!isPickleballEvent ? ' Note that UTR Sports does not support 10 game pro sets or best of 5 sets for posting scores once the event has been played.' : ''}`}
                                                    field={[field, i, 'scoringType']}
                                                    options={isPickleballEvent ? pickleballScoreTypes : getAllowedScoringFormats(tennisScoringTypes, eventTypeId, values[field][i].divisionFormatType, values[field][i].ballTypeValue)}
                                                />
                                            </div>
                                        </div>
                                        {(isTimeBasedFormatEnabled || values.eventDivisions[i].isTimeBased) && (
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <div data-test={`divisionTimeBased`}>
                                                        <ToggleSwitch
                                                            id={`divisions${i}isTimeBased`}
                                                            label="Time Based"
                                                            formGroupCssClass={(showPoweredOnly || values.eventDivisions[i].isTimeBased) ? 'mb4' : ''}
                                                            hoverText={(
                                                                <>
                                                                    <div className="fw600 fs16 mb8">
                                                                        Time Based Match Scoring Format
                                                                    </div>
                                                                    <ul>
                                                                        <li>
                                                                            A timed-based match concludes when the allotted time period is met, regardless of the scoring format or current score.
                                                                        </li>
                                                                        <li>
                                                                            Timed-based matches are required to have a minimum duration of 30 minutes.
                                                                        </li>
                                                                        <li>
                                                                            To count toward a player&apos;s Verified UTR rating, a minimum of 4 games must be won by one player or team.
                                                                        </li>
                                                                    </ul>
                                                                    <div>
                                                                        For more information on Timed-Based match rules, please  <a href={links.verifiedRules} target="_blank" rel="noopener noreferrer">click here</a>.
                                                                    </div>
                                                                </>
                                                            )}
                                                            field={[field, i, 'isTimeBased']}
                                                            reverse={false}
                                                        />
                                                    </div>
                                                </div>
                                                {values.eventDivisions[i].isTimeBased && (
                                                    <div className={`${showPoweredOnly ? 'mb4' : 'form-group'} col-md-12 fs14 fw400`}>
                                                        By enabling Timed-Based Scoring, you confirm that all matches in this division will last at least 30 minutes and follow the <a href={links.verifiedRules} target="_blank" rel="noopener noreferrer">UTR timed-based scoring rules</a>.
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        {showPoweredOnly && (
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <div data-test={`divisionPoweredOnly`}>
                                                        <ToggleSwitch
                                                            id={`divisions${i}annualPoweredOnly`}
                                                            label={ANNUAL_POWER_ONLY_LABEL}
                                                            field={[field, i, 'annualPoweredOnly']}
                                                            reverse={false}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        <div className="row">
                                            <div className="col-md-12">
                                                {isPickleballEvent ? (
                                                    <PBRRangeSliderSmall
                                                        id={`divisions${i}pbr`}
                                                        hoverText={ratingRangeHoverText}
                                                        field={[field, i, 'utrRange']}
                                                        slideStop={val => handleRatingRangeSlideStop(val, i)}
                                                    />
                                                ) : (
                                                    <UTRRangeSliderSmall
                                                        id={`divisions${i}utr`}
                                                        hoverText={ratingRangeHoverText}
                                                        field={[field, i, 'utrRange']}
                                                        slideStop={val => handleRatingRangeSlideStop(val, i)}
                                                    />
                                                )}
                                                <div className={`row`}>
                                                    <div className={`col-5 d-flex flex-column justify-content-center`}>
                                                        <CheckBox
                                                            id={`restrictByRating-${i}`}
                                                            customLabel={'Restrict Rating'}
                                                            field={[field, i, 'restrictByRating']}
                                                            disabled={values.eventDivisions[i].utrRange[0] === minRatingSliderValue && values.eventDivisions[i].utrRange[1] === maxRatingSliderValue}
                                                            data-testid={`event-edit.restrict-rating-${i}`}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-12">
                                                <EventDivisionAgeRangeSliderSmall
                                                    id={`divisions${i}age`}
                                                    hoverText={(
                                                        <ul>
                                                            <li>
                                                                If you pick a specific age range, we recommend you add it in the division name as well.
                                                            </li>
                                                            <li>
                                                                To restrict players from registering for the division outside of your age range, turn on the “Restrict Age” below. When on, only players who fit within the specified age range will be able to register for this division.
                                                            </li>
                                                            <li>
                                                                When “Restrict Age” is off, this acts as purely informational and all players will still be able to register.
                                                            </li>
                                                        </ul>
                                                    )}
                                                    field={[field, i, 'ageRange']}
                                                    slideStop={val => handleAgeRangeSlideStop(val, setValue, i)}
                                                />
                                                <div className={`row`}>
                                                    <div className={`col-5 d-flex flex-column justify-content-center`}>
                                                        <CheckBox
                                                            id={`restrictAge-${i}`}
                                                            customLabel={'Restrict Age'}
                                                            field={[field, i, 'restrictAge']}
                                                            disabled={values.eventDivisions[i].ageRange[0] === 0 && values.eventDivisions[i].ageRange[1] === Object.keys(eventDivisionAgeSliderDictionary).length - 1}
                                                            onChange={val => handleRestrictAgeChange(val, i, values.eventDivisions)}
                                                            data-testid={`event-edit.restrict-age-${i}`}
                                                        />
                                                    </div>
                                                    <div className={`col-7`}>
                                                        <Select
                                                            id={`divisions${i}restrictAgeByDateType`}
                                                            label="Age Limit Deadline"
                                                            field={[field, i, 'restrictAgeByDateType']}
                                                            options={restrictAgeByDateTypes}
                                                            disabled={isRestrictAgeByDateTypeDisabled(values.eventDivisions, i)}
                                                            validate={val => validateRestrictAgeByDateTypeValue(val, values.eventDivisions[i])}
                                                            onChange={val => handleRestrictAgeByDateType(val, i, values.eventDivisions)}
                                                            hoverText={(
                                                                <ul>
                                                                    <li>
                                                                        This selection determines the date for calculating players ages for event registration.
                                                                    </li>
                                                                    <li>
                                                                        If &apos;Event start date&apos; is selected, the events start date determine players age eligibility for divisions with &apos;restrict age&apos; enabled.
                                                                    </li>
                                                                    <li>
                                                                        If &apos;12/31&apos; is selected, the last day of the year (Dec. 31st) determines players age eligibility. For example, players turning 30 by 12/31 can register for a 30+ age division.
                                                                    </li>
                                                                    <li>
                                                                        Once an age limit deadline is set for a division, it automatically applies to all other age-restricted divisions. To change this, adjust the age limit deadline in the initially selected division.
                                                                    </li>
                                                                </ul>
                                                            )}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        {!isFlexLeagueEvent && (
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <Select
                                                        label="Coaching"
                                                        id={`divisions${i}coaching`}
                                                        hoverText="The event will allow in-match coaching"
                                                        field={[field, i, 'coachingType']}
                                                        options={coachingTypes}
                                                    />
                                                </div>
                                                <div className="col-md-6">
                                                    <Select
                                                        label="Officials"
                                                        id={`divisions${i}officials`}
                                                        field={[field, i, 'officialsType']}
                                                        options={officialsTypes}
                                                        hoverText="The event will have on-site officiating"
                                                        disabled={isVerifiedEvent}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div data-test={`divisionAcceptanceListConfig`}>
                                        <DivisionAcceptanceList
                                            key={`division-acceptance-list-${division.id || i}`}
                                            field={field}
                                            formApi={formApi}
                                            division={division}
                                            divisionIndex={i}
                                            sportTypeId={sportTypeId}
                                        />
                                    </div>
                                    <div className="mb-4 mt-3">
                                        <label>Facility Environment</label>
                                        <ReactSelect
                                            isMulti
                                            onChange={val => handleDivisionEnvSurfaceChange(val, 'environments', i)}
                                            value={values.eventDivisions[i].environments}
                                            options={environmentTypes}
                                            placeholder={'Select environments...'}
                                            isClearable={true}
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                        />
                                    </div>
                                    <div className="mb-4 mt-3">
                                        <label>Court Surface</label>
                                        <ReactSelect
                                            isMulti
                                            onChange={val => handleDivisionEnvSurfaceChange(val, 'surfaces', i)}
                                            value={values.eventDivisions[i].surfaces}
                                            options={surfaceTypeOptions}
                                            placeholder={'Select surfaces...'}
                                            isClearable={true}
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                        />
                                    </div>
                                    <div className="mb-4 mt-3">
                                        <label>Division Category (optional)</label>
                                        <ReactSelect
                                            onChange={val => handleDivisionCategoryChange(val, i)}
                                            value={values.eventDivisions[i].divisionCategory === null ? null : values.eventDivisions[i].divisionCategoryModel}
                                            options={divisionCategoryTypeOptions}
                                            placeholder={'Select Category'}
                                            isClearable={true}
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </Tip>
                );
            })}
            <p className="text-small mt24">
                <a
                    href="#"
                    className="underline"
                    onClick={handleAddNewDivision}
                    data-testid="event-edit.divisions.add-division-link"
                >
                    + Add another division
                </a>
            </p>
            {(deleteConfirmation !== null) && (
                <ConfirmActionPopup
                    confirmationTitle="Delete Division"
                    confirmationMessage="Are you sure?"
                    confirmButtonLabel="Yes"
                    onCtaClick={() => deleteDivision(deleteConfirmation)}
                    onCloseClick={() => setDeleteConfirmation(null)}
                    data-testid="modal.delete-division"
                />
            )}
            {(closeRegistrationConfirmation !== null) && (
                <CloseRegistrationConfirmPopup
                    divisionIndex={closeRegistrationConfirmation}
                    onCtaClick={closeDivisionRegistration}
                    onCloseClick={() => {
                        formApi.setValue(['eventDivisions', closeRegistrationConfirmation, 'closeRegistration'], false);
                        setCloseRegistrationConfirmation(null);
                    }}
                />
            )}
        </div>
    );
}

Divisions.propTypes = {
    field: PropTypes.string.isRequired,
    formApi: PropTypes.objectOf(any).isRequired,
    isFlexLeagueEvent: PropTypes.bool,
    allowOffPlatformPayments: PropTypes.bool,
    currency: PropTypes.objectOf(any).isRequired,
    editing: PropTypes.bool,
    registeredPlayers: PropTypes.arrayOf(any).isRequired,
    clubTierType: PropTypes.objectOf(any).isRequired,
    isDemoEvent: PropTypes.bool,
    paymentsEnabled: PropTypes.bool,
    userIsPowered: PropTypes.bool,
    showTip: PropTypes.bool,
    isTemplate: PropTypes.bool,
    onChange: PropTypes.func,
    countryCode: PropTypes.string,
    paymentProviderSetupType: PropTypes.number,
    minimumEntryFee: PropTypes.number,
    formattedMinimumEntryFee: PropTypes.string,
    sportTypeId: PropTypes.number,
    eventTypeId: PropTypes.number,
    showDateError: PropTypes.bool,
};

Divisions.defaultProps = {
    isFlexLeagueEvent: false,
    allowOffPlatformPayments: false,
    editing: false,
    isDemoEvent: false,
    paymentsEnabled: false,
    userIsPowered: false,
    showTip: false,
    isTemplate: false,
    onChange: () => { },
    countryCode: '',
    paymentProviderSetupType: paymentProviderTypeIds.STRIPE,
    minimumEntryFee: 0,
    formattedMinimumEntryFee: '',
    sportTypeId: sportTypeIds.TENNIS,
    showDateError: false,
    eventTypeId: EventTypes.TOURNAMENT,
};

export default Divisions;
